
import { ApiUrlHelperBasic, IApiUrlBasic } from '@ms/odsp-datasources/lib/utilities/url/ApiUrlHelperBasic';
import { SimpleUri } from '@ms/items-view-utilities/lib/uri/SimpleUri';

/**
 * Gets a context for static API calls against target webs.
 * This produces a URL for `${webAbsoluteUrl}/_api`.
 * If calling against an SP Web object, use `getWebApiUrl` instead, since it handles
 * necessary redirection to the root web of an arbitrary path.
 */
export function getStaticApiUrl(params: {
  webAbsoluteUrl: string;
}): IApiUrlBasic {
  return new ApiUrlHelperBasic({
    webAbsoluteUrl: params.webAbsoluteUrl
  }).build();
}

/**
 * Gets a context for API calls against SP Web objects in target webs.
 * This produces a URL equivalent to `${webAbsoluteUrl}/_api/web`.
 *
 * If neither `webAbsoluteUrl` nor `listOrItemFullUrl` is provided, or neither of them contains a domain,
 * this function will throw an error.
 */
export function getWebApiUrl(params: {
  /**
   * The target web. If not available, use `''`, but ensure `listOrItemFUllUrl` is provided instead.
   */
  webAbsoluteUrl: string;
  /**
   * A full path to an item or list in the target web. If not available, use `''`, but ensure `webAbsoluteUrl` is provided.
   */
  listOrItemFullUrl: string;
}): IApiUrlBasic {
  const {
    webAbsoluteUrl,
    listOrItemFullUrl
  } = params;

  const webUrl = webAbsoluteUrl || new SimpleUri(listOrItemFullUrl).authority;

  if (!webUrl) {
    throw new Error('Could not determine the target webUrl to create an API call.');
  }

  let apiUrl = getStaticApiUrl({ webAbsoluteUrl: webUrl });

  if (!webAbsoluteUrl && listOrItemFullUrl) {
    apiUrl = apiUrl.method('SP.RemoteWeb', listOrItemFullUrl).segment('Web');
  } else {
    apiUrl = apiUrl.segment('web');
  }

  return apiUrl;
}

/**
 * Gets a context for API calls against SP List objects for target lists (or the default list).
 * This produces a URL equiavlent to `${webAbsoluteUrl}/_api/web/GetList('${listFullUrl}')`.
 *
 * If neither `webAbsoluteUrl` nor `listFullUrl` is provided, or neither of them contains a domain,
 * this function will throw an error.
 */
export function getListApiUrl(params: {
  /**
   * The target web. If not available, use `''`, but ensure `listOrItemFUllUrl` is provided instead.
   */
  webAbsoluteUrl: string;
  /**
   * A full path to a list in the target web. If not available, use `''`
   * and the default document library for the given web will be selected.
   */
  listFullUrl: string;
}): IApiUrlBasic {
  const {
    webAbsoluteUrl,
    listFullUrl
  } = params;

  const serverRelativeListUrl = new SimpleUri(listFullUrl).path;

  let apiUrl = getWebApiUrl({
    webAbsoluteUrl,
    listOrItemFullUrl: listFullUrl
  });

  if (serverRelativeListUrl) {
    apiUrl = apiUrl.methodWithAliases('GetListUsingPath', {
      DecodedUrl: serverRelativeListUrl
    });
  } else {
    apiUrl = apiUrl.segment('DefaultDocumentLibrary');
  }

  return apiUrl;
}
